import { triggerEvent } from '@transverse/digitalsystems-library'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import fragmentData from '@/data/mega-menu/fragment.json'

type FragmentData = {
  html: string
  css?: string
  script?: string
}

const HIDE_THRESHOLD = 20
const SHOW_THRESHOLD = 15
const FORCE_SHOW_Y = 10
const TRANSITION_MS = 500 // aligné sur le legacy nav-header (.5s)
const COOLDOWN_MS = 600 // > transition pour éviter un re-déclenchement en cours d'animation

function applyTheme(fragment: FragmentData, theme: string): FragmentData {
  if (theme === 'light') return fragment
  return { ...fragment, html: fragment.html.replace('mm-light', `mm-${theme}`) }
}

type MegaMenuProps = {
  // Fragment récupéré côté serveur dans _app.getInitialProps ; fallback bundlé sinon.
  fragment?: FragmentData
}

const MegaMenu = ({ fragment: serverFragment }: MegaMenuProps) => {
  const router = useRouter()
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Capturé une seule fois : les navigations client repassent fragment=undefined, mais
  // le composant persiste dans _app → on conserve le fragment initial (pas de re-injection).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fragment = useMemo(() => applyTheme(serverFragment ?? (fragmentData as FragmentData), 'light'), [])

  // Hydration script injection
  useEffect(() => {
    const source = document.querySelector('script[data-mm-script]')
    if (!source) return
    const code = source.textContent
    if (!code) return

    document.querySelectorAll('script[data-mm-hydration]').forEach((s) => s.remove())
    const el = document.createElement('script')
    el.textContent = code
    el.setAttribute('data-mm-hydration', '')
    document.body.appendChild(el)
  }, [])

  // Scroll hide/show
  const setupScroll = useCallback(() => {
    const wrapper = wrapperRef.current
    const nav = wrapper?.querySelector<HTMLElement>('.mm-nav')
    if (!wrapper || !nav) return

    const navHeight = nav.offsetHeight

    const dispatchHeight = (height: number) => {
      document.dispatchEvent(new CustomEvent('megaMenu', { detail: { height } }))
    }

    nav.style.position = 'relative'
    nav.style.transition = `transform ${TRANSITION_MS}ms ease-in-out`
    wrapper.style.transition = `max-height ${TRANSITION_MS}ms ease-in-out`
    wrapper.style.maxHeight = `${navHeight}px`

    dispatchHeight(navHeight)

    let hidden = false
    let scrollAnchor = 0
    let cooldownUntil = 0

    const hide = () => {
      wrapper.style.overflow = 'hidden'
      wrapper.style.maxHeight = '0px'
      wrapper.style.pointerEvents = 'none'
      nav.style.transform = 'translateY(-100%)'
      hidden = true
      cooldownUntil = Date.now() + COOLDOWN_MS
      dispatchHeight(0)
    }

    const show = () => {
      wrapper.style.maxHeight = `${navHeight}px`
      wrapper.style.pointerEvents = ''
      nav.style.transform = ''
      hidden = false
      cooldownUntil = Date.now() + COOLDOWN_MS
      dispatchHeight(navHeight)

      wrapper.addEventListener(
        'transitionend',
        () => {
          if (!hidden) wrapper.style.overflow = ''
        },
        { once: true }
      )
    }

    const resetStyles = () => {
      nav.style.position = ''
      nav.style.transition = ''
      nav.style.transform = ''
      wrapper.style.transition = ''
      wrapper.style.maxHeight = ''
      wrapper.style.overflow = ''
      wrapper.style.pointerEvents = ''
    }

    const onScroll = () => {
      const y = window.scrollY

      if (nav.classList.contains('mm-overlay-active')) {
        scrollAnchor = y
        return
      }

      if (Date.now() < cooldownUntil) {
        scrollAnchor = y
        return
      }

      if (hidden && y < FORCE_SHOW_Y) {
        window.scrollTo({ top: 0 })
        show()
        scrollAnchor = 0
        return
      }

      if (!hidden) {
        if (y < scrollAnchor) scrollAnchor = y
        if (y - scrollAnchor > HIDE_THRESHOLD && y > navHeight) {
          hide()
          scrollAnchor = y
        }
        return
      }

      if (y > scrollAnchor) scrollAnchor = y
      if (scrollAnchor - y > SHOW_THRESHOLD) {
        show()
        scrollAnchor = y
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      resetStyles()
    }
  }, [])

  useEffect(() => {
    const cleanup = setupScroll()
    if (cleanup) return cleanup

    const observer = new MutationObserver(() => {
      if (wrapperRef.current?.querySelector('.mm-nav')) {
        observer.disconnect()
        setupScroll()
      }
    })
    if (wrapperRef.current) {
      observer.observe(wrapperRef.current, { childList: true, subtree: true })
    }
    return () => observer.disconnect()
  }, [setupScroll])

  // Nav events (navigation + tracking)
  useEffect(() => {
    const handleNavigate = (e: Event) => {
      const { href } = (e as CustomEvent).detail
      if (href) router.push(href)
    }

    const handleTrack = (e: Event) => {
      const { action, label } = (e as CustomEvent).detail ?? {}
      if (action && label) {
        triggerEvent({
          page: 'NavHeader',
          section: 'navigation',
          action: `${action} ${label}`
        })
      }
    }

    window.addEventListener('mm:navigate', handleNavigate)
    window.addEventListener('mm:track', handleTrack)
    return () => {
      window.removeEventListener('mm:navigate', handleNavigate)
      window.removeEventListener('mm:track', handleTrack)
    }
  }, [router])

  return (
    <div ref={wrapperRef} style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
      {fragment.css && <style dangerouslySetInnerHTML={{ __html: fragment.css }} />}
      <div dangerouslySetInnerHTML={{ __html: fragment.html }} />
      {fragment.script && (
        <script type="text/plain" data-mm-script="" dangerouslySetInnerHTML={{ __html: fragment.script }} />
      )}
    </div>
  )
}

export default MegaMenu
